import React, { useContext, useState } from 'react';
import { StateContext } from '../../StateContext';
import {
    Modal,
    Button,
    Box,
    TextField,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { boxStyle } from './styles';
import { addProjectCall } from '../../Services/APICalls/apicalls';
import { hourToSeconds } from '../../General/Links/addWeekOverview';
import { useLanguage } from '../../Services/LanguageContext';

const PROJECT_TYPE_OPTIONS = ['Badkamer', 'Keuken', 'Toilet', 'Divers'];

interface TypeState {
    checked: boolean;
    hours: string;
}

const initTypeState = (): Record<string, TypeState> =>
    Object.fromEntries(PROJECT_TYPE_OPTIONS.map(name => [name, { checked: false, hours: '0' }]));

const AddProject = (props) => {
    const [projectName, setProjectName] = useState('');
    const [typeState, setTypeState] = useState<Record<string, TypeState>>(initTypeState());
    const [description, setDescription] = useState('');
    const [open, setOpen] = useState(false);
    const [materialsDelivered, setMaterialsDelivered] = useState(false);
    const { t } = useLanguage();

    const { loginData, setLoginData } = useContext<any>(StateContext);

    const addProjectHandler = () => {
        const types = PROJECT_TYPE_OPTIONS
            .filter(name => typeState[name].checked)
            .map(name => ({
                name,
                calculatedTimeInSeconds: hourToSeconds(parseFloat(typeState[name].hours) || 0),
            }));
        addProjectCall(
            projectName,
            description,
            0,
            setLoginData,
            materialsDelivered,
            types
        );
        setOpen(false);
        setProjectName('');
        setDescription('');
        setMaterialsDelivered(false);
        setTypeState(initTypeState());
    };

    const toggleType = (name: string, checked: boolean) =>
        setTypeState(prev => ({ ...prev, [name]: { ...prev[name], checked } }));

    const setTypeHours = (name: string, hours: string) =>
        setTypeState(prev => ({ ...prev, [name]: { ...prev[name], hours: hours.replace(',', '.') } }));

    const inputStyle = {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            background: 'var(--bg-tertiary)',
            '& fieldset': { borderColor: 'var(--border-color)' },
            '&:hover fieldset': { borderColor: '#00d9ff' },
            '&.Mui-focused fieldset': {
                borderColor: '#00d9ff',
                boxShadow: '0 0 10px rgba(0, 217, 255, 0.2)',
            },
        },
        '& .MuiInputLabel-root': {
            color: 'var(--text-secondary)',
            '&.Mui-focused': { color: '#00d9ff' },
        },
        '& .MuiOutlinedInput-input': { color: 'var(--text-primary)' },
    };

    const hoursInputStyle = {
        width: '120px',
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            background: 'var(--bg-tertiary)',
            '& fieldset': { borderColor: 'var(--border-color)' },
            '&:hover fieldset': { borderColor: '#00d9ff' },
            '&.Mui-focused fieldset': { borderColor: '#00d9ff' },
        },
        '& .MuiInputLabel-root': {
            color: 'var(--text-secondary)',
            '&.Mui-focused': { color: '#00d9ff' },
        },
        '& .MuiOutlinedInput-input': { color: 'var(--text-primary)' },
    };

    return (
        <div>
            <React.Fragment>
                <Button
                    variant='outlined'
                    color='success'
                    onClick={() => setOpen(true)}
                    sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 600,
                        padding: '10px 24px',
                        borderColor: '#22c55e',
                        color: '#22c55e',
                        '&:hover': {
                            borderColor: '#22c55e',
                            background: 'rgba(34, 197, 94, 0.1)',
                            boxShadow: '0 0 15px rgba(34, 197, 94, 0.3)',
                        },
                    }}
                >
                    + {t('addProject')}
                </Button>
                <Modal
                    open={open}
                    onClose={() => setOpen(false)}
                    aria-labelledby='modal-modal-title'
                    aria-describedby='modal-modal-description'
                    sx={{
                        '& .MuiBackdrop-root': {
                            backdropFilter: 'blur(8px)',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        },
                    }}
                >
                    <Box sx={boxStyle}>
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '3px',
                                background: 'linear-gradient(90deg, #00d9ff, #a855f7)',
                                borderRadius: '16px 16px 0 0',
                            }}
                        />
                        <h2
                            style={{
                                fontSize: '24px',
                                fontWeight: 700,
                                marginBottom: '24px',
                                background: 'linear-gradient(135deg, var(--text-primary), #00d9ff)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            {t('addProject')}
                        </h2>
                        <form
                            onSubmit={(e) => { e.preventDefault(); addProjectHandler(); }}
                            className='flex flex-col items-center gap-4'
                        >
                            <TextField
                                type='text'
                                label={t('project')}
                                value={projectName}
                                onClick={(x) => x.stopPropagation()}
                                onChange={(x) => setProjectName(x.target.value)}
                                sx={inputStyle}
                            />
                            <div style={{ width: '100%' }}>
                                <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '8px' }}>
                                    {t('projectTypes')}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {PROJECT_TYPE_OPTIONS.map(name => (
                                        <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={typeState[name].checked}
                                                        onChange={(e) => toggleType(name, e.target.checked)}
                                                        sx={{
                                                            color: 'var(--text-secondary)',
                                                            '&.Mui-checked': { color: '#00d9ff' },
                                                        }}
                                                    />
                                                }
                                                label={name}
                                                sx={{ color: 'var(--text-primary)', margin: 0, minWidth: '120px' }}
                                            />
                                            {typeState[name].checked && (
                                                <TextField
                                                    size='small'
                                                    type='text'
                                                    label={t('calculatedHours')}
                                                    value={typeState[name].hours}
                                                    inputProps={{ maxLength: 10 }}
                                                    onChange={(e) => setTypeHours(name, e.target.value)}
                                                    sx={hoursInputStyle}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <TextField
                                label={t('remarks')}
                                multiline
                                rows={4}
                                value={description}
                                onChange={(x) => setDescription(x.target.value)}
                                sx={inputStyle}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={materialsDelivered}
                                        onChange={(e) => setMaterialsDelivered(e.target.checked)}
                                        sx={{
                                            color: 'var(--text-secondary)',
                                            '&.Mui-checked': {
                                                color: '#00d9ff',
                                            },
                                        }}
                                    />
                                }
                                label={t('materialsDelivered')}
                                sx={{
                                    color: 'var(--text-primary)',
                                    width: '100%',
                                }}
                            />
                            <button
                                type='submit'
                                style={{
                                    width: '100%',
                                    padding: '14px 24px',
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #00d9ff, #a855f7)',
                                    color: 'white',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    marginTop: '8px',
                                }}
                            >
                                {t('add')}
                            </button>
                        </form>
                    </Box>
                </Modal>
            </React.Fragment>
        </div>
    );
};

export default AddProject;
